import { Alert, AlertDescription } from "@nimit9/signet-ui/components/alert"
import { Button } from "@nimit9/signet-ui/components/button"
import { useState } from "react"
import { useAccounts } from "@/hooks/use-accounts"
import { type MoneyConflict, useConflicts, useResolveConflict } from "@/hooks/use-conflicts"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { useHousehold } from "@/hooks/use-household"
import { api } from "@/lib/api"
import { useUIStore } from "@/store/ui"

type Members = Array<{ id: string; name: string | null; email: string }>

/** Who a user id is: the member's first name, else their email, else "someone". */
export function whoLabel(by: string | null, members: Members): string {
  const m = members.find((x) => x.id === by)
  return (m?.name ?? m?.email)?.split(" ")[0] ?? "someone"
}

/** A conflict value as a person reads it: ₹500, 3 Oct, Debit, HDFC savings. */
export function useValueText() {
  const fmt = useCurrencyFormat()
  const accounts = useAccounts().data?.data ?? []
  return (field: MoneyConflict["field"], v: string | null): string => {
    if (field === "amount") return fmt(Number(v), true)
    if (field === "date")
      return v
        ? new Date(`${v}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
        : ""
    if (field === "type") return v === "credit" ? "Credit" : "Debit"
    return accounts.find((a) => a.id === v)?.name ?? "No account"
  }
}

/** "2 payments need a decision" above the list: opens the first one in the detail panel. */
export function ConflictsBanner() {
  const { data } = useConflicts()
  const openPanel = useUIStore((s) => s.openPanel)
  const [busy, setBusy] = useState(false)
  const rows = data?.data ?? []
  if (rows.length === 0) return null
  const open = async () => {
    setBusy(true)
    try {
      const res = await api.get<{ data: Record<string, unknown> }>(
        `/transactions/${rows[0].transactionId}`,
      )
      openPanel("transaction", { id: rows[0].transactionId, transaction: res.data })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Alert data-testid="conflicts-banner">
      <AlertDescription className="flex items-center justify-between gap-3">
        <span>
          <span className="font-medium">
            {rows.length} {rows.length === 1 ? "payment needs" : "payments need"} a decision
          </span>{" "}
          · two people changed the same payment
        </span>
        <Button size="sm" variant="outline" disabled={busy} onClick={open}>
          Review
        </Button>
      </AlertDescription>
    </Alert>
  )
}

/** The detail panel's "Pick the amount": both values as buttons, one click decides. */
export function ConflictPick({ transactionId }: { transactionId: string }) {
  const { data } = useConflicts()
  const resolve = useResolveConflict()
  const members = useHousehold().data?.members ?? []
  const text = useValueText()
  const [done, setDone] = useState<string[]>([])
  const rows = (data?.data ?? []).filter((c) => c.transactionId === transactionId)
  return (
    <>
      {rows.map((c) => {
        const sides = [
          { v: c.otherValue, by: c.otherBy },
          { v: c.shownValue, by: c.shownBy },
        ]
        return (
          <Alert key={c.id} data-testid="conflict-pick">
            <AlertDescription className="space-y-3">
              <p className="font-medium">
                Pick the {c.field === "accountId" ? "account" : c.field}
              </p>
              <p>
                {sides
                  .map((s) => `${whoLabel(s.by, members)} set ${text(c.field, s.v)}`)
                  .join(" · ")}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {sides.map((s) => (
                  <Button
                    key={`${s.by}-${s.v}`}
                    variant="outline"
                    className="h-auto flex-col items-start py-2"
                    disabled={resolve.isPending}
                    onClick={() =>
                      resolve.mutate(
                        { id: c.id, value: s.v },
                        {
                          onSuccess: () =>
                            setDone((d) => [
                              ...d,
                              `${c.field === "accountId" ? "Account" : c.field[0].toUpperCase() + c.field.slice(1)} set to ${text(c.field, s.v)}`,
                            ]),
                        },
                      )
                    }
                  >
                    <span className="text-base font-semibold">{text(c.field, s.v)}</span>
                    <span className="text-xs text-text-muted">{whoLabel(s.by, members)}</span>
                  </Button>
                ))}
              </div>
              <p className="text-xs text-text-muted">
                Totals use {text(c.field, c.shownValue)} until you pick.
              </p>
            </AlertDescription>
          </Alert>
        )
      })}
      {done.map((d) => (
        <p key={d} role="status" className="text-sm font-medium">
          {d}
        </p>
      ))}
    </>
  )
}
